import { useEffect, useState } from "react";
import type { RefObject } from "react";

/**
 * Custom hook that observes text overflow in an element and provides overflow state
 * @param elementRef - React ref to the element to observe
 * @param dependencies - Array of dependencies to re-run the effect when they change
 * @returns boolean indicating if the element is overflowing
 */
export const useOverflowObserver = <T extends HTMLElement>(
  elementRef: RefObject<T | null>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dependencies: any[] = []
): boolean => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (elementRef.current) {
        const element = elementRef.current;

        const horizontalOverflow = element.offsetWidth < element.scrollWidth;
        const verticalOverflow = element.offsetHeight < element.scrollHeight;

        setIsOverflowing(horizontalOverflow || verticalOverflow);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (document.body) {
      resizeObserver.observe(document.body);
    }

    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return isOverflowing;
};
